import { Link } from "react-router-dom";
import {
  Instagram,
  Twitter,
  Facebook,
  Mail,
  Phone,
  MessageCircle,
  MapPin,
} from "lucide-react";
import useApiData from "../../hooks/useApiData";

const footerLinks = {
  company: [
    { name: "About DVX", path: "/about" },
    { name: "Testimonials", path: "/testimonials" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ],
  support: [
    { name: "FAQ", path: "/faq" },
    { name: "Pricing", path: "/pricing" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Terms of Service", path: "/terms" },
  ],
};

const fallbackData = {
  contact: {},
  social: {},
};

const contactIconMap = {
  email: Mail,
  phone: Phone,
  whatsapp: MessageCircle,
  location: MapPin,
};

const socialIconMap = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
};

const toWhatsAppLink = (value) => {
  const digits = (value || "").replace(/[^\d]/g, "");
  return digits ? `https://wa.me/${digits}` : "";
};

export default function Footer() {
  const { data } = useApiData("/contact-info", fallbackData);
  const contact = data?.contact || {};
  const social = data?.social || {};

  const contactInfo = [
    contact?.support_email && {
      type: "email",
      label: "Email",
      value: contact.support_email,
      href: `mailto:${contact.support_email}`,
    },
    contact?.support_phone && {
      type: "phone",
      label: "Phone",
      value: contact.support_phone,
      href: `tel:${contact.support_phone}`,
    },
    contact?.whatsapp_number && {
      type: "whatsapp",
      label: "WhatsApp",
      value: contact.whatsapp_number,
      href: toWhatsAppLink(contact.whatsapp_number),
    },
    contact?.office_address && {
      type: "location",
      label: "Address",
      value: contact.office_address,
    },
  ].filter(Boolean);

  const socialLinks = Object.entries(social || {})
    .filter(([_, url]) => url)
    .map(([key, url]) => ({
      icon: socialIconMap[key] || Instagram,
      href: url,
      label: key,
    }));

  return (
    <footer data-testid="footer" className="bg-[var(--dvx-surface)] border-t border-[var(--dvx-border)] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="font-oswald text-4xl font-bold text-[var(--dvx-text)]">
              DVX
            </Link>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope leading-relaxed max-w-xs">
              Unleash your power. Build elite strength. Transform your body with
              science-backed programming and proven results.
            </p>
            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className="flex gap-4 mt-6">
                {socialLinks.map((socialItem) => (
                  <a
                    key={socialItem.label}
                    href={socialItem.href}
                    data-testid={`footer-social-${socialItem.label.toLowerCase()}`}
                    className="w-10 h-10 flex items-center justify-center bg-[var(--dvx-bg)] border border-[var(--dvx-border)] text-[var(--dvx-text-muted)] hover:text-[var(--dvx-primary)] hover:border-[var(--dvx-primary)] transition-colors"
                    aria-label={socialItem.label}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <socialItem.icon size={18} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-oswald text-lg font-bold text-[var(--dvx-text)] uppercase tracking-wider mb-4">
              Contact
            </h4>
            {contactInfo.length > 0 ? (
              <ul className="space-y-4">
                {contactInfo.map((info) => {
                  const Icon = contactIconMap[info.type] || Mail;
                  return (
                    <li key={info.label} className="flex items-start gap-3">
                      <Icon size={16} className="mt-0.5 text-[var(--dvx-primary)]" />
                      <div className="text-sm font-manrope">
                        <p className="text-[var(--dvx-text-muted)]">
                          {info.label}
                        </p>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="text-[var(--dvx-text)] hover:text-[var(--dvx-primary)] transition-colors"
                            target={info.type === "whatsapp" ? "_blank" : undefined}
                            rel={info.type === "whatsapp" ? "noreferrer" : undefined}
                          >
                            {info.value}
                          </a>
                        ) : (
                          <span className="text-[var(--dvx-text)]">
                            {info.value}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-[var(--dvx-text-muted)] font-manrope text-sm">
                Contact details coming soon.
              </p>
            )}
          </div>

          {/* Company */}
          <div>
            <h4 className="font-oswald text-lg font-bold text-[var(--dvx-text)] uppercase tracking-wider mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[var(--dvx-text-muted)] hover:text-[var(--dvx-primary)] transition-colors font-manrope text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-oswald text-lg font-bold text-[var(--dvx-text)] uppercase tracking-wider mb-4">
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[var(--dvx-text-muted)] hover:text-[var(--dvx-primary)] transition-colors font-manrope text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-[var(--dvx-border)] flex flex-col md:flex-row justify-center items-center gap-4">
          <p className="text-[var(--dvx-text-muted)] text-sm font-manrope">
            © {new Date().getFullYear()} DVX Fitness. All rights reserved.
          </p>
          {/* <p className="text-[var(--dvx-text-muted)] text-sm font-manrope">
            Built for elite performance.
          </p> */}
        </div>
      </div>
    </footer>
  );
}
